import { createContext, useContext, ReactNode } from 'react'
import { router, usePage } from '@inertiajs/react'
import { useRouteParams } from '@/hooks/useRoutes'
import routes from '@/routeHelpers'

export type FilterOption = 'all' | 'unread' | 'unresolved' | 'most_recent' | 'longest_waiting' | 'resolved'
export type FolderOption = 'inbox' | 'spam' | 'archive'
export type AssignmentOption = 'everyone' | 'mine' | 'unassigned'

interface FilterContextValue {
  currentFilter: FilterOption
  currentFolder: FolderOption
  currentAssignment: AssignmentOption
  setFilter: (filter: FilterOption) => void
  setFolder: (folder: FolderOption) => void
  setAssignment: (assignment: AssignmentOption) => void
}

const LIST_PROPS = ['conversations', 'conversations_next_cursor', 'current_filter', 'current_folder', 'current_assignment', 'flash']

const FilterContext = createContext<FilterContextValue | null>(null)

interface FilterProviderProps {
  children: ReactNode
  currentFilter: FilterOption
  currentFolder?: FolderOption
  currentAssignment?: AssignmentOption
}

export function FilterProvider({
  children,
  currentFilter,
  currentFolder = 'inbox',
  currentAssignment = 'everyone',
}: FilterProviderProps) {
  const { withInbox } = useRouteParams()
  const { component } = usePage()

  const navigateWith = (overrides: Partial<{ filter: FilterOption; folder: FolderOption; assignment: AssignmentOption }>) => {
    const basePath = routes.inbox.show.path(withInbox())
    router.get(
      basePath,
      {
        filter: overrides.filter ?? currentFilter,
        folder: overrides.folder ?? currentFolder,
        assignment: overrides.assignment ?? currentAssignment,
      },
      {
        preserveState: true,
        preserveScroll: true,
        // Already on the Inbox page: only the list changes, so skip re-sending
        // the layout's shared sidebar props. From a conversation this is a
        // real navigation to the Inbox page and needs everything.
        ...(component === 'Inbox' && { only: LIST_PROPS }),
      }
    )
  }

  const setFilter = (filter: FilterOption) => {
    if (filter === currentFilter) return
    navigateWith({ filter })
  }

  const setFolder = (folder: FolderOption) => {
    if (folder === currentFolder) return
    navigateWith({ folder })
  }

  const setAssignment = (assignment: AssignmentOption) => {
    if (assignment === currentAssignment) return
    navigateWith({ assignment })
  }

  return (
    <FilterContext.Provider value={{ currentFilter, currentFolder, currentAssignment, setFilter, setFolder, setAssignment }}>
      {children}
    </FilterContext.Provider>
  )
}

export function useFilter() {
  const context = useContext(FilterContext)
  if (!context) {
    throw new Error('useFilter must be used within a FilterProvider')
  }
  return context
}
